import { useId } from "react";
import { cn } from "@/lib/utils";
import { isCertified } from "@/lib/certification";



function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  // 0° at 12 o'clock; angles increase clockwise
  const angleRad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(angleRad),
    y: cy + r * Math.sin(angleRad),
  };
}

function describeArc(
  cx: number,
  cy: number,
  r: number,
  startAngle: number,
  endAngle: number,
  sweepFlag: 0 | 1
) {
  const start = polarToCartesian(cx, cy, r, startAngle);
  const end = polarToCartesian(cx, cy, r, endAngle);

  const delta = Math.abs(endAngle - startAngle) % 360;
  const largeArcFlag: 0 | 1 = delta > 180 ? 1 : 0;

  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} ${sweepFlag} ${end.x} ${end.y}`;
}

/**
 * Circular Seal Badge - Premium Certification Seal
 * Used on the Cafe Detail page.
 *
 * Goals:
 * - Much larger: 100px on mobile, 120px on desktop
 * - Premium double-ring border
 * - Clean radial background
 * - All text upright/readable (no upside-down text)
 */

interface CircularSealBadgeProps {
  score: number | null;
  size?: "sm" | "browse" | "default" | "hero";
  className?: string;
  /**
   * Force the certified treatment on/off. Defaults to deriving it from the
   * score via isCertified() so a certified café looks certified everywhere.
   */
  certified?: boolean;
}

export function CircularSealBadge({
  score,
  size = "hero",
  className,
  certified,
}: CircularSealBadgeProps) {
  const displayScore = score ?? 0;
  const showCertified = certified ?? isCertified(score);

  const sizeClasses: Record<NonNullable<CircularSealBadgeProps["size"]>, string> = {
    sm: "w-[48px] h-[48px]",
    browse: "w-[65px] h-[65px] sm:w-[74px] sm:h-[74px] md:w-[72px] md:h-[72px]",
    default: "w-[90px] h-[90px]",
    hero: "w-[100px] h-[100px] sm:w-[120px] sm:h-[120px]",
  };





  // Use a 120x120 viewBox so the type sizes map cleanly at 120px render size
  // and scale down proportionally on mobile.
  const viewBox = 120;
  const center = viewBox / 2;

  // Rings (in viewBox units ~ px at 120px render size)
  // Certified cafés get a heavier gold ring.
  const outerRadius = 56;
  const outerStrokeWidth = showCertified ? 5 : 3;

  // Inner ring inside the outer ring.
  const innerRadius = outerRadius - (showCertified ? 6 : 4);
  const innerStrokeWidth = 1;

  const uid = useId();
  const gradientId = `sealBgGradient-${uid}`;
  const topArcId = `sealTopArc-${uid}`;
  const bottomArcId = `sealBottomArc-${uid}`;

  const textRadius = 40;
  // Top: left→right along top curve; Bottom: left→right along bottom curve
  const topArcPath = describeArc(center, center, textRadius, 300, 420, 1);
  const bottomArcPath = describeArc(center, center, textRadius, 240, 120, 0);

  return (
    <div
      className={cn("relative flex-shrink-0", sizeClasses[size], className)}
      style={{
        filter: showCertified
          ? "drop-shadow(0px 6px 22px hsl(var(--seal-gold) / 0.45))"
          : "drop-shadow(0px 6px 20px hsl(var(--seal-gold) / 0.25))",
      }}
    >

      <svg viewBox={`0 0 ${viewBox} ${viewBox}`} className="w-full h-full">
        <defs>
          <radialGradient id={gradientId} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="hsl(var(--seal-bg-center))" />
            <stop offset="100%" stopColor="hsl(var(--seal-bg-edge))" />
          </radialGradient>

          <path id={topArcId} d={topArcPath} fill="none" />
          <path id={bottomArcId} d={bottomArcPath} fill="none" />
        </defs>

        {/* Outer ring */}
        <circle
          cx={center}
          cy={center}
          r={outerRadius}
          fill="none"
          stroke={showCertified ? "hsl(var(--seal-gold))" : "hsl(var(--border))"}
          strokeWidth={outerStrokeWidth}
        />

        {/* Inner ring */}
        <circle
          cx={center}
          cy={center}
          r={innerRadius}
          fill="none"
          stroke="hsl(var(--seal-cream))"
          strokeWidth={innerStrokeWidth}
        />

        {/* Background */}
        <circle
          cx={center}
          cy={center}
          r={innerRadius - 0.5}
          fill={`url(#${gradientId})`}
        />

        {/* GROUNDED (top, arced, upright) */}
        <text
          fill="hsl(var(--seal-gold-ink))"
          fontSize={12}
          fontWeight={700}
          letterSpacing="1px"
          textAnchor="middle"
          style={{ textTransform: "uppercase" }}
        >
          <textPath href={`#${topArcId}`} startOffset="50%" dy="2">
            GROUNDED
          </textPath>
        </text>

        {/* Score (center) */}
        <text
          x={center}
          y={center + 6}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="hsl(var(--seal-ink))"
          fontSize={58}
          fontWeight={800}
          letterSpacing="-1px"
          fontFamily="'DM Serif Display', serif"
        >
          {displayScore}
        </text>

        {/* SCORE (bottom, arced, upright) */}
        <text
          fill="hsl(var(--seal-gold-ink))"
          fontSize={12}
          fontWeight={700}
          letterSpacing="1px"
          textAnchor="middle"
          style={{ textTransform: "uppercase" }}
        >
          <textPath href={`#${bottomArcId}`} startOffset="50%" dy="-2">
            SCORE
          </textPath>
        </text>
      </svg>


    </div>

  );
}
